import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { getSupabase } from "@/lib/supabase";

/**
 * RequireAuth — guarda de rota real do app cliente (/app/*).
 *
 * Regra (Item A — correção prioridade alta): nenhuma tela dentro de
 * `AppLayout` (Header, Sidebar, páginas) pode montar — e portanto nenhum
 * fallback mockado pode aparecer — antes de confirmarmos uma sessão válida.
 *
 * Fluxo:
 *  1. Lê a sessão viva via supabase.auth.getSession() (com fallback de
 *     refresh) — nunca confia apenas no `app_session` legado (forjável).
 *  2. Revalida no backend GET /api/auth/me com o access_token.
 *  3. Qualquer falha — sem sessão, 401, 429 (rate limit), erro de rede ou
 *     5xx — é tratada como "sem sessão" (fail closed) e redireciona para o
 *     login. Nada de cair num estado intermediário com dados mockados.
 *  4. Se a sessão for válida, aguarda brevemente o `app_session` (bridge
 *     Supabase → contexto legado) hidratar antes de liberar o render, para
 *     que Header/TenantContext já nasçam com o usuário real.
 */
const APP_SESSION_KEY = "app_session";

export default function RequireAuth({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | allowed | no-session

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data } = await sb.auth.getSession();
        let token = data?.session?.access_token;
        if (!token) {
          try {
            const { data: r } = await sb.auth.refreshSession();
            token = r?.session?.access_token;
          } catch { /* sem refresh token válido */ }
        }
        if (!token) { if (!cancelled) setStatus("no-session"); return; }

        const base = process.env.REACT_APP_BACKEND_URL;
        let res;
        try {
          res = await fetch(`${base}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch {
          // Falha de rede — fail closed, não deixa a tela passar com mock.
          if (!cancelled) setStatus("no-session");
          return;
        }
        if (cancelled) return;
        // Qualquer status != 2xx (401 expirado, 429 rate-limit, 5xx) é
        // tratado como sessão inválida — nunca renderiza o app com mock.
        if (!res.ok) { setStatus("no-session"); return; }
        await res.json().catch(() => null);

        // Sessão confirmada. Aguarda brevemente o sessionBridge escrever o
        // `app_session` legado (usado por TenantContext/Header) para reduzir
        // o flash de dados ainda não hidratados no primeiro paint.
        await new Promise((resolve) => {
          const start = Date.now();
          const tick = () => {
            if (cancelled) return resolve();
            if (localStorage.getItem(APP_SESSION_KEY)) return resolve();
            if (Date.now() - start > 1000) return resolve(); // fallback
            setTimeout(tick, 60);
          };
          tick();
        });
        if (!cancelled) setStatus("allowed");
      } catch {
        if (!cancelled) setStatus("no-session");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="auth-guard-checking" aria-busy="true">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
          <span className="text-sm text-slate-500">Verificando sua sessão…</span>
        </div>
      </div>
    );
  }

  if (status === "no-session") {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
}
